import React, { useState, useEffect } from 'react';
import { X, User, Phone, MapPin, Calendar, CreditCard, Mail, AlertCircle } from 'lucide-react';
import { Member, Town, Contribution } from '../../types';
import { formatCurrency } from '../../utils/currency';
import { HelpButton } from '../Help/HelpButton';
import { extractRawMemberId } from '../../utils/memberIdUtils';
import { apiClient } from '../../services/api-client';
import { API_CONFIG } from '../../config/api';

interface MonthlyAssignment {
  id: string;
  member_id: string;
  month: number;
  year: number;
  assigned_amount: number;
  actual_contributions: number;
  rollover_amount: number;
  status: string;
}

interface MemberDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member | null;
  town: Town | null;
  contributions: Contribution[];
  onEdit: (member: Member) => void;
  onSimulate?: (member: Member) => void;
}

export const MemberDetailModal: React.FC<MemberDetailModalProps> = ({
  isOpen,
  onClose,
  member,
  town,
  contributions,
  onEdit,
  onSimulate
}) => {
  const [assignments, setAssignments] = useState<MonthlyAssignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !member) return;
    setAssignments([]);
    setAssignmentsLoading(true);
    apiClient.get<MonthlyAssignment[]>(
      API_CONFIG.ENDPOINTS.ASSIGNMENTS_HISTORY,
      { memberId: extractRawMemberId(member.id) }
    ).then(res => {
      if (res.success && res.data) setAssignments(Array.isArray(res.data) ? res.data : []);
    }).finally(() => setAssignmentsLoading(false));
  }, [isOpen, member?.id]);

  if (!isOpen || !member) return null;

  const memberId = extractRawMemberId(member.id);
  const memberContributions = contributions
    .filter(c => extractRawMemberId(c.memberId) === memberId)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const completedContributions = memberContributions.filter(c => c.status === 'Completed');

  // Use member properties if available, fallback to calculations for legacy data
  const totalContributions = member.totalContributions || memberContributions.reduce((sum, c) => sum + c.amount, 0);
  // Use lastPaymentDate from member if available
  const lastPaymentDate = member.lastPaymentDate || (memberContributions.length > 0 ? memberContributions[0].date : undefined);
  const lastPaymentAmount = memberContributions.length > 0 && memberContributions[0].date === lastPaymentDate
    ? memberContributions[0].amount
    : (member.totalContributions > 0 ? 'N/A' : 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-primary rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
            <User className="h-6 w-6 mr-2 text-blue-600" />
            Member Profile
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Member Info Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative group">
                {member.profilePhoto ? (
                  <img
                    src={member.profilePhoto}
                    alt={member.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-blue-200"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{member.name}</h3>
                <p className="text-gray-600 dark:text-dark-text-secondary">{member.username}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-dark-text-secondary">{town?.name}</span>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => onEdit(member)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Edit Profile
              </button>
              {onSimulate && (
                <button
                  onClick={() => onSimulate(member)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                  title="View as this member"
                >
                  View Portal
                </button>
              )}
              <HelpButton
                guideId="members/financial-information"
                variant="icon"
                size="md"
              />
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Total Contributions</p>
                  <p className="text-xl font-bold text-green-700 dark:text-green-300">{formatCurrency(totalContributions)}</p>
                </div>
                <CreditCard className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 dark:text-blue-400">Total Payments</p>
                  <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{completedContributions.length}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 dark:text-purple-400">Member Since</p>
                  <p className="text-xl font-bold text-purple-700 dark:text-purple-300">{member.registrationDate}</p>
                </div>
                <User className="h-8 w-8 text-purple-600" />
              </div>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 dark:text-red-400">Total Arrears</p>
                  <p className="text-xl font-bold text-red-700 dark:text-red-300">
                    {assignmentsLoading ? '—' : formatCurrency(assignments.reduce((sum, a) => sum + Math.max(0, (a.assigned_amount ?? 0) - (a.actual_contributions ?? 0)), 0))}
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>
          </div>

          {/* Member Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Personal Information</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Phone:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">{member.phone}</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Email:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">
                      {member.email || 'Not set'}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <User className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Gender:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">{member.gender}</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Age Group:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">{member.ageGroup}</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Date of Birth:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">
                      {member.dateOfBirth ? new Date(member.dateOfBirth).toLocaleDateString() : 'Not set'}
                    </span>
                    {member.dateOfBirth && (
                      <div className="text-xs text-gray-500 dark:text-dark-text-tertiary ml-6">
                        Age: {Math.floor((new Date().getTime() - new Date(member.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))} years
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Town:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">{town?.name}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Location & Identity</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">House Location:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">
                      {member.houseLocation || 'Not set'}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Home Town:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">
                      {member.homeTown || 'Not set'}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CreditCard className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Ghana Card:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">
                      {member.ghanaCardNumber || 'Not set'}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CreditCard className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">NHIS Number:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">
                      {member.nhisNumber || 'Not set'}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Registration:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary ml-2">{member.registrationDate}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Payment Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-dark-bg-secondary rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Last Payment Date:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                    {lastPaymentDate || 'Never'}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg-secondary rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Last Payment Amount:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                    {typeof lastPaymentAmount === 'number' ? formatCurrency(lastPaymentAmount) : lastPaymentAmount}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Dues History */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Monthly Dues History</h4>
            <div className="bg-gray-50 dark:bg-dark-bg-secondary rounded-lg p-4">
              {assignmentsLoading ? (
                <p className="text-gray-500 dark:text-dark-text-secondary text-center py-4">Loading dues history...</p>
              ) : (() => {
                // Build full month timeline from registration to now, merging with fetched assignments
                const regDate = member.registrationDate ? new Date(member.registrationDate) : new Date();
                const now = new Date();
                const aMap = new Map<string, MonthlyAssignment>();
                assignments.forEach(a => aMap.set(`${a.year}-${Number(a.month)}`, a));

                const rows: { key: string; year: number; month: number; assignment: MonthlyAssignment | null }[] = [];
                let cur = new Date(regDate.getFullYear(), regDate.getMonth(), 1);
                const end = new Date(now.getFullYear(), now.getMonth(), 1);
                while (cur <= end) {
                  const y = cur.getFullYear();
                  const m = cur.getMonth() + 1;
                  rows.push({ key: `${y}-${m}`, year: y, month: m, assignment: aMap.get(`${y}-${m}`) || null });
                  cur.setMonth(cur.getMonth() + 1);
                }
                rows.reverse(); // newest first

                const statusStyles: Record<string, string> = {
                  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                  overdue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                  partial: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
                  skipped: 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400',
                };

                return rows.length > 0 ? (
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 dark:bg-dark-bg-secondary">
                        <tr className="text-left text-gray-500 dark:text-dark-text-tertiary border-b border-gray-200 dark:border-dark-border-primary">
                          <th className="pb-2 pr-4">Month</th>
                          <th className="pb-2 pr-4">Assigned</th>
                          <th className="pb-2 pr-4">Paid</th>
                          <th className="pb-2">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-dark-border-primary">
                        {rows.map(({ key, year, month, assignment }) => {
                          const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'short', year: 'numeric' });
                          const status = assignment ? assignment.status : 'skipped';
                          const assignedAmt = assignment ? (assignment.assigned_amount ?? 0) : 0;
                          const paidAmt = assignment ? (assignment.actual_contributions ?? 0) : 0;
                          return (
                            <tr key={key}>
                              <td className="py-2 pr-4 text-gray-900 dark:text-dark-text-primary">{monthName}</td>
                              <td className="py-2 pr-4 text-gray-900 dark:text-dark-text-primary">
                                {assignment ? formatCurrency(assignedAmt) : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="py-2 pr-4 text-gray-900 dark:text-dark-text-primary">
                                {assignment ? formatCurrency(paidAmt) : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
                                  {status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-dark-text-secondary text-center py-4">No dues history available</p>
                );
              })()}
            </div>
          </div>

          {/* Recent Contributions */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Recent Contributions</h4>
            <div className="bg-gray-50 dark:bg-dark-bg-secondary rounded-lg p-4">
              {memberContributions.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {memberContributions.slice(0, 10).map((contribution) => (
                    <div key={contribution.id} className="flex items-center justify-between p-3 bg-white dark:bg-dark-bg-primary rounded border border-gray-200 dark:border-dark-border-primary">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{contribution.type}</p>
                        <p className="text-xs text-gray-500 dark:text-dark-text-tertiary">
                          {contribution.date} • {contribution.paymentMethod}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
                          {formatCurrency(contribution.amount)}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded-full ${contribution.status === 'Completed'
                          ? 'bg-green-100 text-green-800'
                          : contribution.status === 'Pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                          }`}>
                          {contribution.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-dark-text-secondary text-center py-4">No contributions recorded yet</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-dark-border-primary">
          <button
            onClick={onClose}
            className="bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-hover-bg text-gray-800 dark:text-dark-text-primary py-2 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};